/**
 * Usage Tracking Service
 * Tracks feature usage for plan limits (AI chat requests, AR scans)
 * Stores usage in Supabase with monthly reset logic
 */

import { supabase } from '../lib/supabaseClient';
import SubscriptionService from './subscriptionService';

export interface UsageRecord {
  id?: string;
  user_id: string;
  feature_type: 'ai_chat' | 'ar_scan';
  usage_count: number;
  billing_cycle_start: string; // Date string (e.g., "2026-01-24")
  billing_cycle_end: string; // Date string (e.g., "2026-02-23")
  created_at?: string;
  updated_at?: string;
}

class UsageTrackingService {
  private static instance: UsageTrackingService;
  private subscriptionService = SubscriptionService.getInstance();

  private constructor() {}

  static getInstance(): UsageTrackingService {
    if (!UsageTrackingService.instance) {
      UsageTrackingService.instance = new UsageTrackingService();
    }
    return UsageTrackingService.instance;
  }

  /**
   * Calculate current billing cycle dates based on subscription start date
   * Example: If user subscribed on Jan 24, billing cycles are:
   * - Jan 24 - Feb 23
   * - Feb 24 - Mar 23
   * - etc.
   */
  private async getCurrentBillingCycle(): Promise<{ start: Date; end: Date } | null> {
    const subscriptionStartDate = await this.subscriptionService.getSubscriptionStartDate();
    if (!subscriptionStartDate) return null;

    const now = new Date();
    const dayOfMonth = subscriptionStartDate.getDate();
    
    // Calculate current cycle start
    let cycleStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
    
    // If we're before the cycle day this month, go back one month
    if (now.getDate() < dayOfMonth) {
      cycleStart = new Date(now.getFullYear(), now.getMonth() - 1, dayOfMonth);
    }
    
    // Calculate cycle end (day before next cycle starts)
    let cycleEnd = new Date(cycleStart);
    cycleEnd.setMonth(cycleEnd.getMonth() + 1);
    cycleEnd.setDate(cycleEnd.getDate() - 1);
    
    return { start: cycleStart, end: cycleEnd };
  }

  /**
   * Format date as YYYY-MM-DD string
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get or create usage record for current billing cycle
   */
  private async getUsageRecord(featureType: 'ai_chat' | 'ar_scan'): Promise<UsageRecord | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const billingCycle = await this.getCurrentBillingCycle();
      if (!billingCycle) return null;

      const cycleStartStr = this.formatDate(billingCycle.start);
      const cycleEndStr = this.formatDate(billingCycle.end);

      // Try to get existing record for this billing cycle
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('*')
        .eq('user_id', user.id)
        .eq('feature_type', featureType)
        .eq('billing_cycle_start', cycleStartStr)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
        console.error('[UsageTracking] Error fetching usage:', error);
        return null;
      }

      if (data) {
        return data;
      }

      // Create new record for this billing cycle
      const { data: newRecord, error: insertError } = await supabase
        .from('usage_tracking')
        .insert({
          user_id: user.id,
          feature_type: featureType,
          usage_count: 0,
          billing_cycle_start: cycleStartStr,
          billing_cycle_end: cycleEndStr
        })
        .select()
        .single();

      if (insertError) {
        console.error('[UsageTracking] Error creating usage record:', insertError);
        return null;
      }

      return newRecord;
    } catch (error) {
      console.error('[UsageTracking] Error:', error);
      return null;
    }
  }

  /**
   * Get current usage count for a feature
   */
  async getUsageCount(featureType: 'ai_chat' | 'ar_scan'): Promise<number> {
    const record = await this.getUsageRecord(featureType);
    return record?.usage_count || 0;
  }

  /**
   * Increment usage count for a feature
   */
  async incrementUsage(featureType: 'ai_chat' | 'ar_scan'): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const billingCycle = await this.getCurrentBillingCycle();
      if (!billingCycle) return false;

      const record = await this.getUsageRecord(featureType);
      if (!record) return false;

      const cycleStartStr = this.formatDate(billingCycle.start);

      const { error } = await supabase
        .from('usage_tracking')
        .update({
          usage_count: record.usage_count + 1,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('feature_type', featureType)
        .eq('billing_cycle_start', cycleStartStr);

      if (error) {
        console.error('[UsageTracking] Error incrementing usage:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[UsageTracking] Error:', error);
      return false;
    }
  }

  /**
   * Check if user can use a feature (hasn't hit limit)
   */
  async canUseFeature(featureType: 'ai_chat' | 'ar_scan'): Promise<{ allowed: boolean; remaining: number; limit: number | null }> {
    const permissions = await this.subscriptionService.getFeaturePermissions();
    
    // Get the appropriate limit
    const limit = featureType === 'ai_chat' ? permissions.aiChatLimit : permissions.scanRoomLimit;

    // Unlimited access
    if (limit === null) {
      return { allowed: true, remaining: -1, limit: null };
    }

    // Check current usage
    const currentUsage = await this.getUsageCount(featureType);
    const remaining = Math.max(0, limit - currentUsage);
    const allowed = currentUsage < limit;

    return { allowed, remaining, limit };
  }

  /**
   * Get usage summary for all features
   */
  async getUsageSummary(): Promise<{
    aiChat: { used: number; limit: number | null; remaining: number };
    arScan: { used: number; limit: number | null; remaining: number };
  }> {
    const permissions = await this.subscriptionService.getFeaturePermissions();
    const aiChatUsage = await this.getUsageCount('ai_chat');
    const arScanUsage = await this.getUsageCount('ar_scan');

    return {
      aiChat: {
        used: aiChatUsage,
        limit: permissions.aiChatLimit,
        remaining: permissions.aiChatLimit !== null ? Math.max(0, permissions.aiChatLimit - aiChatUsage) : -1
      },
      arScan: {
        used: arScanUsage,
        limit: permissions.scanRoomLimit,
        remaining: permissions.scanRoomLimit !== null ? Math.max(0, permissions.scanRoomLimit - arScanUsage) : -1
      }
    };
  }

  /**
   * Reset usage for a feature (admin function, or for testing)
   */
  async resetUsage(featureType: 'ai_chat' | 'ar_scan'): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const billingCycle = await this.getCurrentBillingCycle();
      if (!billingCycle) return false;

      const cycleStartStr = this.formatDate(billingCycle.start);

      const { error } = await supabase
        .from('usage_tracking')
        .update({
          usage_count: 0,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('feature_type', featureType)
        .eq('billing_cycle_start', cycleStartStr);

      if (error) {
        console.error('[UsageTracking] Error resetting usage:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[UsageTracking] Error:', error);
      return false;
    }
  }
}

export default UsageTrackingService;
